import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { PatientLanguageSwitcher } from './PatientLanguageSwitcher';
import { resolvePatientLocale } from '@/features/patient-public/patientLocale';

/**
 * PatientLayout — Phase 4e.
 *
 * Clean, public-facing layout used ONLY by the QR-anonymous patient routes
 * (`/p/:token`, `/q/:shortCode`, `/p/:token/reorder`, `/p/:token/reorder/success`).
 *
 * Distinct from `AppShell` (which is the B2B sidebar layout for authenticated
 * podiatrists/admins/providers). No sidebar, no navigation links into the
 * authenticated app, no user menu — the only context patients see is the
 * INSOLECH brand mark and the clinic name (passed in as a prop).
 *
 * Design constraints (Phase 4e brief §7):
 *   - RTL-safe (Arabic-first patients)
 *   - Mobile-first (most QR scans happen on phones)
 *   - High contrast, accessible (WCAG AA)
 *   - Larger, more readable typography than the dense B2B app
 */

interface Props {
  /** Clinic name shown next to the INSOLECH brand mark in the header. */
  clinicName?: string | null;
  children: React.ReactNode;
}

export function PatientLayout({ clinicName, children }: Props) {
  const { t, i18n } = useTranslation('patient');

  // Anonymous patients have no LanguageSwitcher in the authed shell, and the app
  // boots at lng:'en', so without this they always saw English. Resolve the
  // desired locale (stored explicit choice -> browser-detected -> en) once on
  // mount and switch; the global DirectionEffect flips <html dir> for Arabic.
  React.useEffect(() => {
    const desired = resolvePatientLocale();
    if (desired !== i18n.language) void i18n.changeLanguage(desired);
    // Run once on mount; changeLanguage is a no-op if already on `desired`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-screen bg-surface-base flex flex-col">
      <header className="border-b border-border-subtle bg-surface-raised/80 backdrop-blur sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-3 flex items-center gap-3">
          <a
            href="https://insolech.com"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-3 group"
            aria-label="INSOLECH"
          >
            <img
              src="/brand/logo-insolech.png"
              alt="INSOLECH"
              className="h-8 sm:h-10 w-auto"
            />
          </a>
          <div className="ms-auto flex items-center gap-3">
            <PatientLanguageSwitcher />
            {clinicName ? (
              <div className="text-end">
                <p className="text-[11px] uppercase tracking-wider text-text-muted">
                  {t('header_from', 'From')}
                </p>
                <p className="text-sm font-medium text-text-primary truncate max-w-[160px] sm:max-w-[200px]">
                  {clinicName}
                </p>
              </div>
            ) : null}
          </div>
        </div>
      </header>

      <main className="flex-1 px-4 sm:px-6 py-6 sm:py-10 max-w-3xl w-full mx-auto">
        {children}
      </main>

      <footer className="border-t border-border-subtle py-6 text-center text-xs text-text-muted">
        <p>
          INSOLECH ·{' '}
          <a
            href="https://insolech.com"
            target="_blank"
            rel="noopener noreferrer"
            className="underline hover:text-text-primary"
          >
            insolech.com
          </a>
        </p>
        <p className="mt-1 max-w-md mx-auto px-4">
          {t(
            'footer_privacy',
            'This page is private to your clinic. Do not share the URL, anyone with the link can see your order status.',
          )}
        </p>
      </footer>
    </div>
  );
}
